// Two players: a room to share, a code to enter, or one screen handed back and
// forth. The one door that unfolds in place.

import type { CatalogEntry } from "src/api";
import { type BoardMapId } from "src/engine/rules/maps";
import { MAX_NAME_LENGTH } from "src/engine/net/protocol";

import { Btn, Field, SectionTitle, styled } from "../kit";
import { ChipChoice } from "./ChipChoice";
import { DoorBody, DoorRow } from "./DoorCard";

const Named = styled("label")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.kit.space.xs
}));

export function VersusDoor({
  maps,
  map,
  onMap,
  joining,
  onJoining,
  input,
  onInput,
  onJoin,
  onCreate,
  busy,
  name,
  onName,
  onHotSeat
}: {
  /** The board maps (`catalog.maps()`), as the chip row wants them: a name and one sentence. */
  maps: readonly CatalogEntry[];
  map: BoardMapId;
  onMap(map: BoardMapId): void;
  joining: boolean;
  onJoining(joining: boolean): void;
  input: string;
  onInput(input: string): void;
  onJoin(): void;
  onCreate(): void;
  busy: boolean;
  name: string;
  onName(name: string): void;
  onHotSeat(): void;
}) {
  return (
    <DoorBody>
      {/* **Which board, and only where it decides anything.** It rides with
          Create and hot-seat because those are the two ways of *opening* a
          game; joining takes whatever board the room was opened on, and a
          picker above a code box would be a control that quietly does
          nothing. */}
      {!joining && (
        <ChipChoice
          label="Board"
          value={map}
          // The chip row speaks ids; the map's own union is `BoardMapId`.
          onChange={(next) => onMap(next as BoardMapId)}
          options={maps}
          testPrefix="board-map"
        />
      )}

      {!joining && (
        <DoorRow>
          <Btn
            data-testid="mode-create"
            disabled={busy}
            onClick={onCreate}
            style={{ flex: 1 }}
          >
            Create
          </Btn>
          <Btn data-testid="mode-join" onClick={() => onJoining(true)} style={{ flex: 1 }}>
            Join
          </Btn>
        </DoorRow>
      )}

      {joining && (
        <DoorRow>
          <Field
            autoFocus
            placeholder="Game link or code"
            aria-label="Game link or code"
            data-testid="join-input"
            value={input}
            style={{ flex: 1 }}
            onChange={(event) => onInput(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && onJoin()}
          />
          <Btn variant="primary" data-testid="join-go" onClick={onJoin}>
            Go
          </Btn>
        </DoorRow>
      )}

      <Named>
        <SectionTitle>Your name</SectionTitle>
        <Field
          data-testid="name-input"
          maxLength={MAX_NAME_LENGTH}
          value={name}
          onChange={(event) => onName(event.target.value)}
        />
      </Named>

      {/* The sub-option: same screen, no room, no link. It is still two players
          against each other, which is what this card is about — it just skips
          the part where they are apart. */}
      <Btn
        variant="ghost"
        small
        data-testid="mode-local"
        style={{ alignSelf: "flex-start" }}
        onClick={onHotSeat}
      >
        or share one screen
      </Btn>
    </DoorBody>
  );
}
